import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import { api, brl } from "@/lib/api";
import { planModuleItems, startingPrice, useModuleCatalog } from "@/lib/modules";
import { cn } from "@/lib/utils";
import { PlanDetailsDialog, planLimits } from "@/components/landing/PlanDetailsDialog";
import { CustomPlanDialog } from "@/components/landing/CustomPlanDialog";

function PlanCard({ p, onOpen }) {
  const l = planLimits(p);
  const lines = p.custom ? ["Você escolhe os módulos", l[0], l[3], p.support && `Suporte: ${p.support}`].filter(Boolean)
    : [l[0], l[3], `${p.modules.length} módulos`, p.support && `Suporte: ${p.support}`].filter(Boolean);
  return (
    <button type="button" onClick={onOpen} className={cn("card-surface card-hover group flex flex-col p-6 gap-4 text-left", p.highlight && "border-primary", p.custom && "border-dashed border-primary/60")} data-testid={`landing-plan-${p.id}`}>
      <h3 className="font-display text-xl font-bold">{p.name}</h3>
      <p>{p.custom && <span className="text-sm text-muted-foreground">a partir de </span>}<b className="font-display text-3xl">{brl(p.custom ? startingPrice(p) : p.price)}</b><span className="text-muted-foreground">/mês</span></p>
      <ul className="space-y-1.5 text-sm flex-1">
        {lines.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">{p.custom ? "Montar meu plano" : "Ver módulos do plano"}<ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></span>
    </button>
  );
}

// Teste grátis: só existe na página quando o Admin Master liga "Visível na página de planos" (a API devolve null).
function TrialCard({ t, catalog }) {
  const l = planLimits({ max_users: t.limits.users, max_products: t.limits.products, max_orders: t.limits.orders, max_stores: t.limits.stores });
  const mods = planModuleItems(t.modules, catalog);
  return (
    <div className="card-surface flex flex-col p-6 gap-4" data-testid="landing-plan-trial">
      <h3 className="font-display text-xl font-bold">Teste grátis</h3>
      <p><b className="font-display text-3xl">Grátis</b><span className="text-muted-foreground"> por {t.days} {t.days === 1 ? "dia" : "dias"}</span></p>
      <ul className="space-y-1.5 text-sm">
        {l.map((f) => <li key={f} className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}
        <li className="flex gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{mods.length} {mods.length === 1 ? "módulo" : "módulos"}</li>
      </ul>
      <div className="flex flex-wrap gap-1.5 flex-1 content-start" data-testid="landing-plan-trial-modules">
        {mods.map((m) => <span key={m.id} className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">{m.label}</span>)}
      </div>
      <Link to="/cadastro" className="inline-flex items-center gap-1 text-sm font-medium text-primary" data-testid="landing-plan-trial-signup">Começar teste grátis<ChevronRight className="h-4 w-4" /></Link>
    </div>
  );
}

export function LandingPlans() {
  const [plans, setPlans] = useState([]);
  const [trial, setTrial] = useState(null);
  const [open, setOpen] = useState(null);
  const catalog = useModuleCatalog();
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/public/trial-plan").then((r) => setTrial(r.data && r.data.limits ? r.data : null)).catch(() => {}); }, []);
  if (!plans.length) return null;
  const sel = plans.find((p) => p.id === open);
  const close = () => setOpen(null);
  return (
    <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="landing-plans">
      <p className="eyebrow mb-2">Planos</p>
      <h2 className="text-3xl sm:text-4xl font-bold mb-3">Escolha quando quiser. Comece grátis.</h2>
      <p className="text-muted-foreground mb-10">Clique em um plano para ver os módulos incluídos.</p>
      <div className={cn("grid sm:grid-cols-2 gap-4", trial ? "lg:grid-cols-3 xl:grid-cols-5" : "lg:grid-cols-4")}>
        {trial && <TrialCard t={trial} catalog={catalog} />}
        {plans.map((p) => <PlanCard key={p.id} p={p} onOpen={() => setOpen(p.id)} />)}
      </div>
      {sel && (sel.custom ? <CustomPlanDialog plan={sel} catalog={catalog} onClose={close} /> : <PlanDetailsDialog plan={sel} catalog={catalog} onClose={close} />)}
    </section>
  );
}
